/**
 * Bundle Product Sale — section script.
 *
 * Single source of truth: `state`. Every interaction mutates state, then
 * calls render(state). No other function touches the DOM outside render()
 * and the one-time static wiring at the bottom.
 *
 * Cart integration: after a successful /cart/add.js call, this dispatches the
 * same `cart:updated` / `cart:refresh` custom events (bubbling on
 * document.documentElement) that Focal's own <product-form> dispatches after
 * a native add-to-cart. Focal's <cart-drawer> element listens for
 * `cart:refresh` and opens itself when `detail.openMiniCart` is true — so no
 * custom drawer UI is built here.
 */
(function () {
  'use strict';

  function initBundle(root) {
    var sectionId = root.getAttribute('data-section-id');
    var configScript = root.querySelector('script[data-bps-config="' + sectionId + '"]');
    if (!configScript) return;

    var config;
    try {
      config = JSON.parse(configScript.textContent);
    } catch (e) {
      console.error('Bundle product sale: could not parse config JSON', e);
      return;
    }

    var product = config.product;
    var tiers = config.tiers || [];
    var sizePosition = config.sizePosition;
    var colorPosition = config.colorPosition;
    var sleeveLabel = config.sleeveLabel || 'Sleeve';

    var sizeOptionValues = [];
    var colorOptionValues = [];
    (product.options || []).forEach(function (option) {
      if (option.position === sizePosition) sizeOptionValues = option.values || [];
      if (option.position === colorPosition) colorOptionValues = option.values || [];
    });

    var els = {
      root: root,
      mainImages: root.querySelectorAll('[data-bps="main-image"]'),
      colorImage: root.querySelector('[data-bps="color-image"]'),
      thumbs: root.querySelectorAll('[data-bps="thumb"]'),
      tiers: root.querySelectorAll('[data-bps="tier"]'),
      price: root.querySelector('[data-bps="price"]'),
      compare: root.querySelector('[data-bps="compare"]'),
      savings: root.querySelector('[data-bps="savings"]'),
      submit: root.querySelector('[data-bps="submit"]')
    };

    // ---------------------------------------------------------------------
    // Money formatting (self-contained; does not rely on theme globals).
    // ---------------------------------------------------------------------
    function formatMoney(dollarsAmount) {
      var cents = Math.round(parseFloat(dollarsAmount || 0) * 100);
      if (isNaN(cents)) cents = 0;
      var format = config.moneyFormat || '${{amount}}';
      var placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
      var key = placeholderMatch ? placeholderMatch[1] : 'amount';
      var noDecimals = key.indexOf('no_decimals') !== -1;
      var amount = (cents / 100).toFixed(noDecimals ? 0 : 2);
      var parts = amount.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      var value = parts[1] ? dollars + '.' + parts[1] : dollars;
      if (key.indexOf('comma_separator') !== -1) {
        value = value.replace(/,/g, '#').replace(/\./g, ',').replace(/#/g, '.');
      }
      return placeholderMatch ? format.replace(placeholderMatch[0], value) : format;
    }

    // ---------------------------------------------------------------------
    // Variant lookup helpers
    // ---------------------------------------------------------------------
    function optionKey(position) {
      return 'option' + position;
    }

    function findVariant(size, color) {
      var sizeKey = sizePosition ? optionKey(sizePosition) : null;
      var colorKey = colorPosition ? optionKey(colorPosition) : null;
      return product.variants.find(function (variant) {
        var sizeMatches = !sizeKey || variant[sizeKey] === size;
        var colorMatches = !colorKey || variant[colorKey] === color;
        return sizeMatches && colorMatches;
      });
    }

    function findFirstAvailableCombo() {
      var sizes = sizeOptionValues.length ? sizeOptionValues : [null];
      var colors = colorOptionValues.length ? colorOptionValues : [null];
      var fallback = null;
      for (var s = 0; s < sizes.length; s++) {
        for (var c = 0; c < colors.length; c++) {
          var variant = findVariant(sizes[s], colors[c]);
          if (variant) {
            if (!fallback) fallback = { size: sizes[s], color: colors[c], variant: variant };
            if (variant.available) {
              return { size: sizes[s], color: colors[c], variant: variant };
            }
          }
        }
      }
      // Nothing available at all: fall back to the first valid combo found —
      // never render an empty/unselected default.
      return fallback;
    }

    function firstVariantImageForColor(color) {
      if (!color) return null;
      var colorKey = colorPosition ? optionKey(colorPosition) : null;
      if (!colorKey) return null;
      var match = product.variants.find(function (variant) {
        return variant[colorKey] === color && variant.featuredImage;
      });
      return match ? match.featuredImage : null;
    }

    // ---------------------------------------------------------------------
    // State
    // ---------------------------------------------------------------------
    var defaultTier = tiers.find(function (t) { return t.defaultSelected; }) || tiers[0];

    var state = {
      tierId: defaultTier ? defaultTier.id : null,
      sleeves: [],
      activeMediaId: els.mainImages.length ? els.mainImages[0].getAttribute('data-media-id') : null,
      colorOverrideImage: null,
      freeGift: null
    };

    function currentTier() {
      return tiers.find(function (t) { return t.id === state.tierId; }) || tiers[0];
    }

    function buildSleeve() {
      var combo = findFirstAvailableCombo();
      if (!combo) return { size: null, color: null, variantId: null, available: false };
      return {
        size: combo.size,
        color: combo.color,
        variantId: combo.variant.id,
        available: combo.variant.available
      };
    }

    function resizeSleevesForTier() {
      var tier = currentTier();
      if (!tier) return;
      var targetLength = tier.quantity + (tier.freeSleeves || 0);
      while (state.sleeves.length < targetLength) {
        state.sleeves.push(buildSleeve());
      }
      if (state.sleeves.length > targetLength) {
        state.sleeves = state.sleeves.slice(0, targetLength);
      }
    }

    // ---------------------------------------------------------------------
    // Free gift — a separate product from the main tier/sleeves, chosen via
    // its own variant picker. State is rebuilt whenever the active tier
    // changes so switching tiers back and forth doesn't carry over a
    // selection that belongs to a different tier's gift product.
    // ---------------------------------------------------------------------
    function findGiftVariant(tier, options) {
      if (!tier || !tier.freeGift) return null;
      return tier.freeGift.variants.find(function (variant) {
        return Object.keys(options).every(function (position) {
          return variant['option' + position] === options[position];
        });
      });
    }

    function findGiftVariantImageForValue(tier, position, value) {
      if (!tier || !tier.freeGift) return null;
      var match = tier.freeGift.variants.find(function (variant) {
        return variant['option' + position] === value && variant.image;
      });
      return match ? match.image : null;
    }

    function resizeFreeGiftForTier() {
      var tier = currentTier();
      if (!tier || !tier.freeGift) {
        state.freeGift = null;
        return;
      }
      if (state.freeGift && state.freeGift.tierId === tier.id) return;

      var options = {};
      var defaultVariant = tier.freeGift.variants.find(function (v) { return v.id === tier.freeGift.defaultVariantId; });
      (tier.freeGift.options || []).forEach(function (option) {
        options[option.position] = defaultVariant ? defaultVariant['option' + option.position] : null;
      });

      state.freeGift = {
        tierId: tier.id,
        variantId: tier.freeGift.defaultVariantId,
        options: options
      };
    }

    function selectGiftOption(position, value) {
      if (!state.freeGift) return;
      state.freeGift.options[position] = value;
      var variant = findGiftVariant(currentTier(), state.freeGift.options);
      state.freeGift.variantId = variant ? variant.id : null;
      render();
    }

    resizeSleevesForTier();
    resizeFreeGiftForTier();

    // ---------------------------------------------------------------------
    // Actions — mutate state, then render(). Nothing else touches the DOM.
    // ---------------------------------------------------------------------
    function selectTier(tierId) {
      if (state.tierId === tierId) return;
      state.tierId = tierId;
      resizeSleevesForTier();
      resizeFreeGiftForTier();
      render();
    }

    function selectSleeveOption(index, type, value) {
      var sleeve = state.sleeves[index];
      if (!sleeve) return;
      sleeve[type] = value;
      var variant = findVariant(sleeve.size, sleeve.color);
      sleeve.variantId = variant ? variant.id : null;
      sleeve.available = variant ? variant.available : false;

      if (type === 'color') {
        state.colorOverrideImage = firstVariantImageForColor(value) || product.featuredImage;
      }

      render();
    }

    function copyFirstToAll() {
      var first = state.sleeves[0];
      if (!first) return;
      state.sleeves.forEach(function (sleeve, index) {
        if (index === 0) return;
        sleeve.size = first.size;
        sleeve.color = first.color;
        var variant = findVariant(sleeve.size, sleeve.color);
        sleeve.variantId = variant ? variant.id : null;
        sleeve.available = variant ? variant.available : false;
      });
      render();
    }

    function selectMedia(mediaId) {
      state.activeMediaId = mediaId;
      state.colorOverrideImage = null;
      render();
    }

    // ---------------------------------------------------------------------
    // Render — the ONLY function that writes to the DOM after init.
    // ---------------------------------------------------------------------
    function render() {
      var tier = currentTier();
      if (!tier) return;

      renderTiers(tier);
      renderSleeves(tier);
      renderFreeGift(tier);
      renderGallery();
      renderAddToCart(tier);
    }

    function renderTiers(tier) {
      els.tiers.forEach(function (tierEl) {
        var isSelected = tierEl.getAttribute('data-tier-id') === state.tierId;
        tierEl.classList.toggle('is-selected', isSelected);

        var toggle = tierEl.querySelector('[data-bps="tier-toggle"]');
        if (toggle) toggle.setAttribute('aria-checked', isSelected ? 'true' : 'false');

        var panel = tierEl.querySelector('[data-bps="panel"]');
        if (panel) panel.hidden = !isSelected;
      });
    }

    function renderSleeves(tier) {
      var container = root.querySelector('[data-bps="sleeves"][data-tier-id="' + tier.id + '"]');
      if (!container) return;
      container.innerHTML = '';

      state.sleeves.forEach(function (sleeve, index) {
        container.appendChild(buildSleeveRow(sleeve, index, index >= tier.quantity));
      });

      var copyAllButton = root.querySelector('[data-bps="tier"][data-tier-id="' + tier.id + '"] [data-bps="copy-all"]');
      if (copyAllButton) copyAllButton.hidden = (tier.quantity + (tier.freeSleeves || 0)) <= 1;
    }

    function buildSleeveRow(sleeve, index, isFree) {
      var wrapper = document.createElement('div');
      wrapper.className = 'bps-sleeve';
      wrapper.setAttribute('data-bps', 'sleeve');
      wrapper.classList.toggle('is-free', !!isFree);

      var header = document.createElement('div');
      header.className = 'bps-sleeve__header';

      var titleGroup = document.createElement('span');
      titleGroup.className = 'bps-sleeve__title-group';

      var label = document.createElement('span');
      label.className = 'bps-sleeve__label';
      label.textContent = sleeveLabel + ' ' + (index + 1);
      titleGroup.appendChild(label);

      if (isFree) {
        var freeBadge = document.createElement('span');
        freeBadge.className = 'bps-sleeve__free-badge';
        freeBadge.textContent = 'Free';
        titleGroup.appendChild(freeBadge);
      }

      header.appendChild(titleGroup);

      var summary = document.createElement('span');
      summary.className = 'bps-sleeve__summary';
      var summaryParts = [];
      if (sleeve.size) summaryParts.push(sleeve.size);
      if (sleeve.color) summaryParts.push(sleeve.color);
      summary.textContent = summaryParts.join(' · ') || 'Select options';
      if (!sleeve.available) {
        var tag = document.createElement('span');
        tag.className = 'is-sold-out-tag';
        tag.textContent = ' · Sold out';
        summary.appendChild(tag);
      }
      header.appendChild(summary);
      wrapper.appendChild(header);

      if (sizeOptionValues.length) {
        var sizeRow = document.createElement('div');
        sizeRow.className = 'bps-sleeve__sizes';
        sizeOptionValues.forEach(function (sizeValue) {
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'bps-size-button';
          button.textContent = sizeValue;
          var variant = findVariant(sizeValue, sleeve.color);
          var isSoldOut = !variant || !variant.available;
          button.classList.toggle('is-sold-out', isSoldOut);
          button.classList.toggle('is-selected', sleeve.size === sizeValue);
          // Always clickable, even sold out, per spec.
          button.addEventListener('click', function () {
            selectSleeveOption(index, 'size', sizeValue);
          });
          sizeRow.appendChild(button);
        });
        wrapper.appendChild(sizeRow);
      }

      if (colorOptionValues.length) {
        var colorRow = document.createElement('div');
        colorRow.className = 'bps-sleeve__colors';
        colorOptionValues.forEach(function (colorValue) {
          var swatch = document.createElement('button');
          swatch.type = 'button';
          swatch.className = 'bps-color-swatch';
          swatch.setAttribute('aria-label', colorValue);
          var variant = findVariant(sleeve.size, colorValue);
          var isSoldOut = !variant || !variant.available;
          var image = firstVariantImageForColor(colorValue);
          if (image) {
            swatch.style.backgroundImage = 'url(' + image + ')';
          } else {
            swatch.classList.add('bps-color-swatch--no-image');
          }
          swatch.classList.toggle('is-sold-out', isSoldOut);
          swatch.classList.toggle('is-selected', sleeve.color === colorValue);
          swatch.addEventListener('click', function () {
            selectSleeveOption(index, 'color', colorValue);
          });
          colorRow.appendChild(swatch);
        });
        wrapper.appendChild(colorRow);
      }

      return wrapper;
    }

    function renderFreeGift(tier) {
      var container = root.querySelector('[data-bps="free-gift"][data-tier-id="' + tier.id + '"]');
      if (!container) return;
      container.innerHTML = '';
      if (!tier.freeGift || !state.freeGift) return;

      var header = document.createElement('div');
      header.className = 'bps-gift__header';

      if (tier.freeGift.productImage) {
        var img = document.createElement('img');
        img.className = 'bps-gift__image';
        img.src = tier.freeGift.productImage;
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        header.appendChild(img);
      }

      var titleWrap = document.createElement('div');
      titleWrap.className = 'bps-gift__title-wrap';

      var title = document.createElement('p');
      title.className = 'bps-gift__title';
      title.textContent = tier.freeGift.productTitle || '';
      titleWrap.appendChild(title);

      var badge = document.createElement('span');
      badge.className = 'bps-gift__badge';
      badge.textContent = 'Free gift';
      titleWrap.appendChild(badge);

      header.appendChild(titleWrap);
      container.appendChild(header);

      (tier.freeGift.options || []).forEach(function (option) {
        if (!option.values || option.values.length <= 1) return;

        var row = document.createElement('div');
        row.className = 'bps-gift__options';

        var label = document.createElement('span');
        label.className = 'bps-gift__options-label';
        label.textContent = option.name;
        row.appendChild(label);

        var buttonsWrap = document.createElement('div');
        buttonsWrap.className = 'bps-gift__options-buttons';

        var isColorOption = (option.name || '').toLowerCase() === 'color';

        option.values.forEach(function (value) {
          var testOptions = {};
          Object.keys(state.freeGift.options).forEach(function (position) {
            testOptions[position] = state.freeGift.options[position];
          });
          testOptions[option.position] = value;
          var variant = findGiftVariant(tier, testOptions);
          var isSoldOut = !variant || !variant.available;
          var isSelected = state.freeGift.options[option.position] === value;
          var image = isColorOption ? findGiftVariantImageForValue(tier, option.position, value) : null;

          var button = document.createElement('button');
          button.type = 'button';

          if (image) {
            button.className = 'bps-gift__color-swatch';
            button.setAttribute('aria-label', value);
            button.style.backgroundImage = 'url(' + image + ')';
          } else {
            button.className = 'bps-gift__option-button';
            button.textContent = value;
          }

          button.classList.toggle('is-sold-out', isSoldOut);
          button.classList.toggle('is-selected', isSelected);
          // Always clickable, even sold out, per spec (matches sleeve size buttons).
          button.addEventListener('click', function () {
            selectGiftOption(option.position, value);
          });
          buttonsWrap.appendChild(button);
        });

        row.appendChild(buttonsWrap);
        container.appendChild(row);
      });
    }

    function renderGallery() {
      if (state.colorOverrideImage && els.colorImage) {
        els.mainImages.forEach(function (img) { img.classList.remove('is-active'); });
        els.colorImage.src = state.colorOverrideImage;
        els.colorImage.classList.add('is-active');
      } else {
        if (els.colorImage) els.colorImage.classList.remove('is-active');
        els.mainImages.forEach(function (img) {
          img.classList.toggle('is-active', img.getAttribute('data-media-id') === state.activeMediaId);
        });
      }

      els.thumbs.forEach(function (thumb) {
        var isSelected = !state.colorOverrideImage && thumb.getAttribute('data-media-id') === state.activeMediaId;
        thumb.classList.toggle('is-selected', isSelected);
      });
    }

    function renderAddToCart(tier) {
      if (els.price) els.price.textContent = formatMoney(tier.price);

      var tierEl = root.querySelector('[data-bps="tier"][data-tier-id="' + tier.id + '"]');
      var compareAtPrice = tierEl ? tierEl.getAttribute('data-tier-compare-at') : '';
      var savingsText = tierEl ? tierEl.getAttribute('data-tier-savings') : '';

      if (els.compare) {
        if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(tier.price)) {
          els.compare.textContent = formatMoney(compareAtPrice);
          els.compare.hidden = false;
        } else {
          els.compare.hidden = true;
        }
      }

      if (els.savings) {
        if (savingsText) {
          els.savings.textContent = savingsText;
          els.savings.hidden = false;
        } else {
          els.savings.hidden = true;
        }
      }

      var giftUnavailable = false;
      if (tier.freeGift) {
        var selectedGiftVariant = tier.freeGift.variants.find(function (variant) {
          return variant.id === (state.freeGift && state.freeGift.variantId);
        });
        giftUnavailable = !selectedGiftVariant || !selectedGiftVariant.available;
      }

      var anyUnavailable = giftUnavailable || state.sleeves.some(function (sleeve) { return !sleeve.available; });

      if (els.submit) {
        els.submit.disabled = anyUnavailable;
        els.submit.classList.toggle('is-sold-out', anyUnavailable);
        els.submit.textContent = anyUnavailable
          ? els.submit.getAttribute('data-label-sold-out')
          : 'Add ' + tier.label + ' to cart · ' + formatMoney(tier.price);
      }
    }

    // ---------------------------------------------------------------------
    // Static interaction wiring (one-time; never touches the DOM directly
    // beyond what render() already covers).
    // ---------------------------------------------------------------------
    els.tiers.forEach(function (tierEl) {
      var toggle = tierEl.querySelector('[data-bps="tier-toggle"]');
      if (toggle) {
        toggle.addEventListener('click', function () {
          selectTier(tierEl.getAttribute('data-tier-id'));
        });
      }
      var copyAllButton = tierEl.querySelector('[data-bps="copy-all"]');
      if (copyAllButton) {
        copyAllButton.addEventListener('click', copyFirstToAll);
      }
    });

    els.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        selectMedia(thumb.getAttribute('data-media-id'));
      });
    });

    if (els.submit) {
      els.submit.addEventListener('click', addToCart);
    }

    // ---------------------------------------------------------------------
    // Add to cart
    // ---------------------------------------------------------------------
    function addToCart() {
      if (els.submit.disabled) return;

      var bundleId = Date.now() + '-' + Math.floor(Math.random() * 1e6);
      var tier = currentTier();

      var paidSleeves = state.sleeves.slice(0, tier.quantity);
      var freeSleeves = state.sleeves.slice(tier.quantity);

      // Merge duplicate variant selections among paid sleeves into a single line with quantity 2+.
      var mergedByVariant = {};
      paidSleeves.forEach(function (sleeve) {
        if (!sleeve.variantId) return;
        mergedByVariant[sleeve.variantId] = (mergedByVariant[sleeve.variantId] || 0) + 1;
      });

      var items = Object.keys(mergedByVariant).map(function (variantId) {
        return {
          id: parseInt(variantId, 10),
          quantity: mergedByVariant[variantId],
          properties: {
            _bundle_id: bundleId,
            _bundle_tier: tier.label
          }
        };
      });

      // Free sleeves (Buy X Get Y) are always their own unmerged line, even when
      // they match a paid variant, so a 100%-off Automatic Discount targeting the
      // `_free_gift` property can zero out exactly this line and nothing else.
      freeSleeves.forEach(function (sleeve) {
        if (!sleeve.variantId) return;
        items.push({
          id: parseInt(sleeve.variantId, 10),
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_tier: tier.label,
            _free_gift: 'true'
          }
        });
      });

      if (!items.length) return;

      // Free gift for this tier, if configured — uses whichever variant the
      // shopper picked in the gift's own option buttons (state.freeGift),
      // not just the first available one. Price is not overridden here —
      // the merchant sets up a 100%-off Automatic Discount on this product in
      // Admin, same pattern as the tier bundle pricing.
      if (tier.freeGift && state.freeGift && state.freeGift.variantId) {
        items.push({
          id: parseInt(state.freeGift.variantId, 10),
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_tier: tier.label,
            _free_gift: 'true'
          }
        });
      }

      els.submit.setAttribute('aria-busy', 'true');
      els.submit.disabled = true;

      var addUrl = (config.routes.cartAddUrl || '/cart/add') + '.js';
      var cartUrl = config.routes.cartUrl || '/cart';
      var themeSettings = (window.themeVariables && window.themeVariables.settings) || {};
      var cartType = themeSettings.cartType || 'drawer';

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) { return response.json(); })
        .then(function (addResponse) {
          if (addResponse && addResponse.status) {
            throw new Error(addResponse.description || addResponse.message || 'Add to cart failed');
          }
          return fetch(cartUrl + '.js').then(function (cartResponse) { return cartResponse.json(); });
        })
        .then(function (cartContent) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: cartContent }
          }));

          // This store's cart drawer is provided by the AMP Slide Cart app,
          // which manages its own UI independently of Focal's native
          // <cart-drawer> and never listens for `cart:refresh`. It exposes a
          // public API on `window` for exactly this: feed it the fresh cart,
          // then ask it to open. Fall back to Focal's own drawer mechanism
          // (or a redirect to the cart page) if that app isn't present, so
          // this section stays portable to other stores/setups.
          if (typeof window.SLIDECART_OPEN === 'function') {
            if (typeof window.SLIDECART_UPDATE === 'function') {
              try { window.SLIDECART_UPDATE(cartContent); } catch (e) { /* ignore */ }
            }
            window.SLIDECART_OPEN();
            return;
          }

          if (cartType === 'page') {
            window.location.href = cartUrl;
            return;
          }

          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart: cartContent, openMiniCart: cartType === 'drawer' }
          }));
        })
        .catch(function (error) {
          console.error('Bundle product sale: add to cart failed', error);
        })
        .then(function () {
          els.submit.removeAttribute('aria-busy');
          render();
        });
    }

    // Initial paint.
    render();
  }

  // ---------------------------------------------------------------------
  // Countdown timer block — independent of the bundle state machine above;
  // it doesn't affect price/variant logic, just an evergreen urgency banner.
  // Each visitor gets their own countdown (persisted in localStorage keyed
  // per block) that restarts automatically once it reaches zero, so the
  // section works indefinitely without a merchant having to set real dates.
  // ---------------------------------------------------------------------
  function initCountdown(el) {
    var durationSeconds = parseInt(el.getAttribute('data-duration-seconds'), 10) || 21600;
    var storageKey = el.getAttribute('data-storage-key');
    var hoursEl = el.querySelector('[data-bps="countdown-hours"]');
    var minutesEl = el.querySelector('[data-bps="countdown-minutes"]');
    var secondsEl = el.querySelector('[data-bps="countdown-seconds"]');

    function readExpiry() {
      try {
        var stored = window.localStorage.getItem(storageKey);
        var expiry = stored ? parseInt(stored, 10) : NaN;
        if (!isNaN(expiry) && expiry > Date.now()) return expiry;
      } catch (e) {
        // localStorage unavailable (privacy mode, etc.) — fall through to a
        // fresh in-memory countdown for this page view.
      }
      return null;
    }

    function writeExpiry(expiry) {
      try { window.localStorage.setItem(storageKey, String(expiry)); } catch (e) { /* ignore */ }
    }

    var expiry = readExpiry();
    if (!expiry) {
      expiry = Date.now() + durationSeconds * 1000;
      writeExpiry(expiry);
    }

    function pad(value) {
      return value < 10 ? '0' + value : String(value);
    }

    function tick() {
      var remainingMs = expiry - Date.now();
      if (remainingMs <= 0) {
        expiry = Date.now() + durationSeconds * 1000;
        writeExpiry(expiry);
        remainingMs = expiry - Date.now();
      }
      var totalSeconds = Math.floor(remainingMs / 1000);
      var hours = Math.floor(totalSeconds / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
    }

    tick();
    setInterval(tick, 1000);
  }

  // ---------------------------------------------------------------------
  // Short description "Read more" — the toggle only appears if the text is
  // actually clamped (checked via scrollHeight vs clientHeight), so short
  // descriptions never show a pointless "Read more" that expands nothing.
  // ---------------------------------------------------------------------
  function initShortDescription(content) {
    var toggle = content.nextElementSibling;
    if (!toggle || toggle.getAttribute('data-bps') !== 'short-description-toggle') return;

    function checkOverflow() {
      if (content.classList.contains('is-expanded')) return;
      toggle.hidden = content.scrollHeight <= content.clientHeight + 1;
    }

    toggle.addEventListener('click', function () {
      var expanded = !content.classList.contains('is-expanded');
      content.classList.toggle('is-expanded', expanded);
      toggle.textContent = expanded
        ? toggle.getAttribute('data-label-less')
        : toggle.getAttribute('data-label-more');
      if (!expanded) checkOverflow();
    });

    checkOverflow();
    window.addEventListener('load', checkOverflow);
    window.addEventListener('resize', checkOverflow);
  }

  // ---------------------------------------------------------------------
  // Estimate shipping — country flag/code isn't known at Liquid render time
  // (no request geo-IP in a theme), so it's resolved client-side from the
  // visitor's IP via ipapi.co, once per page load, and applied to every
  // instance of this block. Falls back to the configured label with the
  // country code stripped if the lookup fails (ad blockers, offline, etc.).
  // ---------------------------------------------------------------------
  function initShippingCountry() {
    var targets = document.querySelectorAll('[data-bps="shipping-country"]');
    if (!targets.length) return;

    function applyToTarget(el, code, countryName) {
      var template = el.getAttribute('data-label-template') || 'Fast [code] Shipping';
      el.innerHTML = '';

      if (code) {
        var img = document.createElement('img');
        img.className = 'bps-shipping__flag';
        img.src = 'https://flagcdn.com/w20/' + code.toLowerCase() + '.png';
        img.alt = (countryName || '') + ' flag';
        img.width = 18;
        img.height = 12;
        img.loading = 'eager';
        el.appendChild(img);
      }

      var parts = template.split('[code]');
      el.appendChild(document.createTextNode(parts[0]));
      if (code) {
        var strong = document.createElement('strong');
        strong.textContent = code.toUpperCase();
        strong.style.margin = '0 3px';
        el.appendChild(strong);
      }
      if (parts[1]) el.appendChild(document.createTextNode(parts[1]));
    }

    fetch('https://ipapi.co/json/')
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (!data || !data.country_code) throw new Error('No country code');
        targets.forEach(function (el) {
          applyToTarget(el, data.country_code, data.country_name);
        });
      })
      .catch(function () {
        targets.forEach(function (el) {
          applyToTarget(el, null, null);
        });
      });
  }

  // ---------------------------------------------------------------------
  // Product accordion — each block is an independent expand/collapse item;
  // there's no accordion-wide state, so opening one never closes another.
  // ---------------------------------------------------------------------
  function initAccordionItem(item) {
    var trigger = item.querySelector('[data-bps="accordion-trigger"]');
    var panel = item.querySelector('[data-bps="accordion-panel"]');
    if (!trigger || !panel) return;

    trigger.addEventListener('click', function () {
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      panel.hidden = expanded;
    });
  }

  function init() {
    document.querySelectorAll('.bps').forEach(function (root) {
      if (root.querySelector('.bps-layout')) initBundle(root);
    });
    document.querySelectorAll('[data-bps="countdown"]').forEach(initCountdown);
    document.querySelectorAll('[data-bps="short-description"]').forEach(initShortDescription);
    document.querySelectorAll('[data-bps="accordion-item"]').forEach(initAccordionItem);
    initShippingCountry();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
